import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Download,
  Loader2,
  Database,
  Calendar,
  Clock,
  TrainIcon,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  X,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import DownloadPDF from "../../utils/DownloadPDF.js";
import api from "../../services/AxiosConnection.js";

const TrainDetailsTable = ({
  responseData,
  groupedTrains,
  sites,
  locationPairs,
  downloadLoading,
  setDownloadLoading,
  setValidationError,
}) => {
  const [selectedTrain, setSelectedTrain] = useState(null);
  const [selectedSiteDbName, setSelectedSiteDbName] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [isPopupLoading, setIsPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const popupRef = useRef(null);

  const COLOR_PALETTE = [
    "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
    "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6"
  ];

  const getSiteColor = (siteName) => {
    if (!siteName) return "#3B82F6";
    let hash = 0;
    for (let i = 0; i < siteName.length; i++) {
      hash = siteName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return COLOR_PALETTE[Math.abs(hash) % COLOR_PALETTE.length];
  };

  const formatTrainDate = (trainId) => {
    if (!trainId) return "N/A";
    const parts = trainId.split("_");
    if (parts.length !== 7) return "N/A";
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  const formatTrainTime = (trainId) => {
    if (!trainId) return "N/A";
    const parts = trainId.split("_");
    if (parts.length !== 7) return "N/A";
    const [hour, minute] = parts.slice(3, 5);
    return `${hour}:${minute}`;
  };

  const formatTrainName = (name) => (name === "Train Name" ? "N/A" : name);
  const formatTrainNumber = (number) => (number === "Train Number" ? "N/A" : number);

  const mapSide = (side) => {
    if (["Ts1", "Ts3", "Ts5"].includes(side)) return "Left";
    if (["Ts2", "Ts4", "Ts6"].includes(side)) return "Right";
    return side || "N/A";
  };

  const formatAcknowledgementTime = (time) => {
    if (!time) return "Not Yet Acknowledged";
    try {
      const date = new Date(time);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return "Not Yet Acknowledged";
    }
  };

  const locationLookup = useMemo(() => {
    return (locationPairs || []).reduce((acc, pair) => {
      const [key, value] = pair.split(" = ");
      acc[key] = value;
      return acc;
    }, {});
  }, [locationPairs]);

  const siteDbNameLookup = useMemo(() => {
    return (sites || []).reduce((acc, site) => {
      acc[site.siteName] = site.siteDbName;
      return acc;
    }, {});
  }, [sites]);

  const handleDownload = async (siteDbName, trainId) => {
    if (!siteDbName || !trainId) {
      setValidationError("Missing site or train ID. Please try again.");
      return;
    }

    const site = sites.find((s) => s.siteDbName === siteDbName);
    const displaySiteName = site ? site.siteName : siteDbName;

    setDownloadLoading((prev) => ({ ...prev, [trainId]: true }));

    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await DownloadPDF(siteDbName, displaySiteName, { TrainId: trainId }, (isLoading) => {
        setDownloadLoading((prev) => ({ ...prev, [trainId]: isLoading }));
      });
    } catch (err) {
      console.error("Error downloading PDF:", err);
      setDownloadLoading((prev) => ({ ...prev, [trainId]: false }));
      setValidationError("Failed to download PDF. Please try again.");
    }
  };

  const handleViewDetails = async (siteDbName, trainId) => {
    if (!siteDbName || !trainId) {
      setPopupError("Invalid site or train ID.");
      return;
    }

    setSelectedTrain(trainId);
    setSelectedSiteDbName(siteDbName);
    setIsPopupLoading(true);
    setPopupError(null);

    try {
      const response = await api.get(
        `/simple-view/GetTrainDataByTrainId?siteDb=${siteDbName}&trainId=${trainId}`
      );
      const sortedData = response.data.data
        ? [...response.data.data].sort((a, b) => {
            const axleA = a.TemperatureLogs?.[0]?.AxleNo
              ? parseInt(a.TemperatureLogs[0].AxleNo)
              : 0;
            const axleB = b.TemperatureLogs?.[0]?.AxleNo
              ? parseInt(b.TemperatureLogs[0].AxleNo)
              : 0;
            return axleA - axleB;
          })
        : [];
      const sortedAlarms = response.data.alarms
        ? [...response.data.alarms].sort((a, b) => {
            const axleA = a.AxleNo ? parseInt(a.AxleNo) : 0;
            const axleB = b.AxleNo ? parseInt(b.AxleNo) : 0;
            return axleA - axleB;
          })
        : [];
      const sortedWarnings = response.data.warnings
        ? [...response.data.warnings].sort((a, b) => {
            const axleA = a.AxleNo ? parseInt(a.AxleNo) : 0;
            const axleB = b.AxleNo ? parseInt(b.AxleNo) : 0;
            return axleA - axleB;
          })
        : [];
      setPopupData({ ...response.data, data: sortedData, alarms: sortedAlarms, warnings: sortedWarnings });
    } catch (err) {
      console.error("Error fetching train details:", err);
      setPopupError("Failed to fetch train details. Please try again.");
    } finally {
      setIsPopupLoading(false);
    }
  };

  const closePopup = () => {
    setPopupData(null);
    setSelectedTrain(null);
    setSelectedSiteDbName(null);
    setPopupError(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup();
      }
    };

    if (popupData || isPopupLoading) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupData, isPopupLoading]);

  useEffect(() => {
    if (popupData || isPopupLoading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [popupData, isPopupLoading]);

  useEffect(() => {
    setCurrentPage(1);
  }, [groupedTrains]);

  const allFlatTrains = useMemo(() => {
    return groupedTrains.flatMap(({ siteName, trains }) =>
      trains.map((train) => ({ ...train, siteGroupName: siteName }))
    );
  }, [groupedTrains]);

  const totalEntries = allFlatTrains.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalEntries);
  const paginatedTrains = useMemo(() => {
    return allFlatTrains.slice(startIndex, endIndex);
  }, [allFlatTrains, startIndex, endIndex]);

  if (
    !responseData ||
    !responseData.trainDataBySiteDb ||
    Object.keys(responseData.trainDataBySiteDb).length === 0
  ) {
    return (
      <div className="p-3 text-center">
        <div className="flex flex-col items-center gap-2">
          <Database className="h-8 w-8 text-gray-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">No Data Found</h3>
            <p className="text-[11px] text-gray-600">
              No train data available for the selected filters. Please adjust your search criteria.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 w-full overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto lg:overflow-x-hidden">
          <table id="train-data-table" className="min-w-full divide-y divide-gray-200 lg:table-auto">
            <thead className="bg-gray-50 sticky top-0 z-09">
              <tr>
                {[
                  "Sr. No.",
                  "Site Name",
                  "Train Date",
                  "Train Time",
                  "Train Name",
                  "Train Number",
                  "Train Type",
                  "Direction",
                  "No Of Axle",
                  "Avg Speed",
                  "Alarm",
                  "Warning",
                  "Actions",
                ].map((head, i) => (
                  <th
                    key={i}
                    className="px-1 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTrains.map((train) => (
                <tr
                  key={train.TrainId}
                  onClick={() => handleViewDetails(train.SiteName, train.TrainId)}
                  className={`cursor-pointer hover:bg-gray-50 transition ${selectedTrain === train.TrainId ? "bg-blue-50 border-l-2 border-blue-500" : ""}`}
                >
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">{train.serialNo}</td>
                  <td className="px-1 py-2 whitespace-nowrap">
                    <div className="flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: getSiteColor(train.siteGroupName || train.SiteName) }} />
                      <span className="text-[11px] text-gray-900 truncate text-center">{locationLookup[train.siteGroupName || train.SiteName] || train.SiteName}</span>
                    </div>
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">
                    {formatTrainDate(train.TrainId)}
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">
                    {formatTrainTime(train.TrainId)}
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 truncate text-center">
                    {formatTrainName(train.TrainName)}
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 truncate text-center">
                    {formatTrainNumber(train.TrainNumber)}
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">{train.Description || "N/A"}</td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">{train.Direction || "N/A"}</td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">{train.TotalNoOfAxles || "N/A"}</td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] text-gray-900 text-center">
                    {train.AvgSpeed ? `${train.AvgSpeed} km/h` : "N/A"}
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${train.AlarmType === "hasAlarm" ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}
                    >
                      {train.AlarmType === "hasAlarm" ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${train.WarningType === "hasWarning" ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"}`}
                    >
                      {train.WarningType === "hasWarning" ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-1 py-2 whitespace-nowrap text-[11px] font-medium">
                    <div className="flex space-x-1 justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(train.SiteName, train.TrainId);
                        }}
                        disabled={downloadLoading[train.TrainId]}
                        className="inline-flex items-center justify-center w-6 h-6 border border-gray-300 rounded-md shadow-sm text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadLoading[train.TrainId] ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Download className="h-3 w-3" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(train.SiteName, train.TrainId);
                        }}
                        className="inline-flex items-center justify-center w-6 h-6 border border-gray-300 rounded-md shadow-sm text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="bg-gray-50 px-3 py-2 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 gap-2">
          <div className="flex items-center gap-3 text-xs text-gray-700">
            <span>
              Showing {totalEntries > 0 ? startIndex + 1 : 0} to {endIndex} of {totalEntries} trains
            </span>
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-gray-500">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First Page"
              className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-0.5"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Prev</span>
            </button>
            <span className="text-xs px-2 font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-0.5"
            >
              <span>Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last Page"
              className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {(popupData || isPopupLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div ref={popupRef} className="bg-white rounded-md border border-gray-200 shadow-sm max-w-5xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white rounded-t-md">
              <div className="flex items-center gap-2">
                <TrainIcon className="h-4 w-4 text-blue-600" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Train Details</h2>
                  {popupData && (
                    <p className="text-[11px] text-gray-600 text-center">
                      {formatTrainDate(popupData.trainTransactions?.[0]?.TrainId)} {formatTrainTime(popupData.trainTransactions?.[0]?.TrainId)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {popupData && selectedTrain && selectedSiteDbName && (
                  <button
                    onClick={() => handleDownload(selectedSiteDbName, selectedTrain)}
                    disabled={downloadLoading[selectedTrain] || isPopupLoading}
                    className="inline-flex items-center justify-center w-6 h-6 border border-gray-300 rounded-md shadow-sm text-[10px] font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadLoading[selectedTrain] ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                  </button>
                )}
                <button
                  onClick={closePopup}
                  className="inline-flex items-center justify-center rounded-full h-6 w-6 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3 lg:overflow-x-hidden">
              {isPopupLoading ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600 mb-2" />
                  <p className="text-[11px] text-gray-600 text-center">Loading train details...</p>
                </div>
              ) : popupError ? (
                <div className="rounded-md bg-red-50 p-2">
                  <div className="flex items-center justify-center">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-red-400" />
                    </div>
                    <div className="ml-2">
                      <p className="text-[11px] text-red-700 text-center">{popupError}</p>
                    </div>
                  </div>
                </div>
              ) : (
                popupData && (
                  <>
                    <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-600" />
                          <h3 className="text-base font-semibold text-gray-900">Train Summary Details</h3>
                        </div>
                      </div>
                      <div className="px-3 py-3">
                        {popupData.trainTransactions?.length > 0 ? (
                          <div className="space-y-3">
                            {popupData.trainTransactions.map((train, index) => (
                              <div
                                key={index}
                                className="flex flex-col sm:flex-row gap-3"
                              >
                                <div className="flex-1 min-w-0 max-w-xs bg-gray-50 rounded-md p-3 border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                  <div className="grid grid-cols-1 gap-2 text-[11px]">
                                    {[
                                      {
                                        label: "Train Date & Time",
                                        value: `${formatTrainDate(train.TrainId)} ${formatTrainTime(train.TrainId)}`
                                      },
                                      { label: "Train Name", value: formatTrainName(train.TrainName) },
                                      { label: "Train Number", value: formatTrainNumber(train.TrainNumber) },
                                      { label: "Direction", value: train.Direction || "N/A" },
                                    ].map((item, itemIndex) => (
                                      <div
                                        key={itemIndex}
                                        className="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                                      >
                                        <span className="font-medium text-gray-700 text-center flex-1">{item.label}:</span>
                                        <span className="text-gray-900 font-semibold text-center flex-1">{item.value}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0 max-w-xs bg-gray-50 rounded-md p-3 border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                  <div className="grid grid-cols-1 gap-2 text-[11px]">
                                    {[
                                      {
                                        label: "Average Speed",
                                        value: train.AvgSpeed ? `${Number(train.AvgSpeed).toFixed(2)} km/h` : "N/A",
                                      },
                                      { label: "Number of Coaches", value: train.NoBogi || "N/A" },
                                      { label: "Number of Locos", value: train.NoLoco || "N/A" },
                                      {
                                        label: "Atmospheric Temp",
                                        value: train.AtmosphereTemp ? `${Number(train.AtmosphereTemp).toFixed(2)}°C` : "N/A",
                                      },
                                    ].map((item, itemIndex) => (
                                      <div
                                        key={itemIndex}
                                        className="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                                      >
                                        <span className="font-medium text-gray-700 text-center flex-1">{item.label}:</span>
                                        <span className="text-gray-900 font-semibold text-center flex-1">{item.value}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0 max-w-xs bg-gray-50 rounded-md p-3 border border-gray-200 hover:shadow-md transition-shadow duration-200">
                                  <div className="grid grid-cols-1 gap-2 text-[11px]">
                                    {[
                                      { label: "Number of Axles", value: train.TotalNoOfAxles || "N/A" },
                                      {
                                        label: "Max Axle Temp Diff",
                                        value: train.Max_Avg_Temp_Diff
                                          ? `${Number(train.Max_Avg_Temp_Diff).toFixed(2)}°C`
                                          : "N/A",
                                      },
                                      {
                                        label: "Max Avg Axle Temp",
                                        value: train.Max_Avg_Axle_Temp
                                          ? `${Number(train.Max_Avg_Axle_Temp).toFixed(2)}°C`
                                          : "N/A",
                                      },
                                      { label: "Coach Type", value: train.Description || "N/A" },
                                    ].map((item, itemIndex) => (
                                      <div
                                        key={itemIndex}
                                        className="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                                      >
                                        <span className="font-medium text-gray-700 text-center flex-1">{item.label}:</span>
                                        <span className="text-gray-900 font-semibold text-center flex-1">{item.value}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[11px] text-gray-600 text-center py-2">No train transaction data available</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                      <div className="px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <h3 className="text-base font-semibold text-gray-900">Temperature Details</h3>
                        </div>
                      </div>
                      <div className="px-2 py-3">
                        {popupData.data?.length > 0 ? (
                          <div className="lg:overflow-x-hidden">
                            <table className="min-w-full divide-y divide-gray-200 lg:table-auto">
                              <thead className="bg-gray-50">
                                <tr>
                                  {[
                                    "Axle No",
                                    "Axle Speed",
                                    "Coach Position",
                                    "Coach Number",
                                    "LH Axle (°C)",
                                    "RH Axle (°C)",
                                    "Temp Difference (°C)",
                                    ...(popupData.noOfTempSensors >= 4
                                      ? ["LH Wheel (°C)", "RH Wheel (°C)", "Temp Difference (°C)"]
                                      : []),
                                    ...(popupData.noOfTempSensors >= 6
                                      ? ["LH Brake Disc (°C)", "RH Brake Disc (°C)", "Temp Difference (°C)"]
                                      : []),
                                    ...(popupData.noOfTempSensors >= 2
                                      ? ["LH Side Axle Avg (°C)", "RH Side Axle Avg (°C)", "Whole Coach Axle Avg (°C)"]
                                      : []),
                                    ...(popupData.noOfTempSensors >= 4
                                      ? [
                                          "LH Side Wheel Avg (°C)",
                                          "RH Side Wheel Avg (°C)",
                                          "Whole Coach Wheel Avg (°C)",
                                        ]
                                      : []),
                                    ...(popupData.noOfTempSensors >= 6
                                      ? [
                                          "LH Side Brake Avg (°C)",
                                          "RH Side Brake Avg (°C)",
                                          "Whole Coach Brake Avg (°C)",
                                        ]
                                      : []),
                                  ].map((head, i) => (
                                    <th
                                      key={i}
                                      className="px-1 py-2 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                      {head}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {popupData.data.map((coach, coachIndex) => {
                                  const sortedLogs = (coach.TemperatureLogs || []).sort(
                                    (a, b) => (parseInt(a.AxleNo) || 0) - (parseInt(b.AxleNo) || 0)
                                  );
                                  const tempCalcs =
                                    coach.TempCalculations && coach.TempCalculations.length > 0
                                      ? coach.TempCalculations[0]
                                      : null;

                                  const isSpecial = coach.CoachPosition === "LOCO" || coach.CoachPosition === "WAG12";
                                  let groupSize = null;
                                  let nameGenerator = null;

                                  if (coach.CoachPosition === "LOCO") {
                                    groupSize = 6;
                                    nameGenerator = (idx) => `LOCO ${idx + 1}`;
                                  } else if (coach.CoachPosition === "WAG12") {
                                    groupSize = 4;
                                    nameGenerator = (idx) => `WAG12 ${Math.floor(idx / 2) + 1}.${(idx % 2) + 1}`;
                                  }

                                  if (isSpecial && groupSize) {
                                    let rows = [];
                                    for (let g = 0; g < sortedLogs.length; g += groupSize) {
                                      const group = sortedLogs.slice(g, g + groupSize);
                                      const groupIndex = Math.floor(g / groupSize);
                                      const subPosition = nameGenerator(groupIndex);

                                      group.forEach((log, logIndex) => {
                                        const isFirstInGroup = logIndex === 0;
                                        const isFirstInCoach = g === 0 && logIndex === 0;

                                        rows.push(
                                          <tr key={`${coachIndex}-${g + logIndex}`} className="hover:bg-gray-50">
                                            <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{log.AxleNo || "N/A"}</td>
                                            <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                              {log.AxleSpeed ? Number(log.AxleSpeed).toFixed(2) : "N/A"}
                                            </td>
                                            {isFirstInGroup && (
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle"
                                                rowSpan={group.length}
                                              >
                                                {subPosition}
                                              </td>
                                            )}
                                            {isFirstInCoach && (
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {log.CoachNo || "N/A"}
                                              </td>
                                            )}
                                            <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                              {log.Ts1 ? Number(log.Ts1).toFixed(2) : "N/A"}
                                            </td>
                                            <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                              {log.Ts2 ? Number(log.Ts2).toFixed(2) : "N/A"}
                                            </td>
                                            <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                              {log.Ts1 && log.Ts2
                                                ? Number(Math.abs(log.Ts1 - log.Ts2)).toFixed(2)
                                                : "N/A"}
                                            </td>
                                            {popupData.noOfTempSensors >= 4 && (
                                              <>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts3 ? Number(log.Ts3).toFixed(2) : "N/A"}
                                                </td>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts4 ? Number(log.Ts4).toFixed(2) : "N/A"}
                                                </td>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts3 && log.Ts4
                                                    ? Number(Math.abs(log.Ts3 - log.Ts4)).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                              </>
                                            )}
                                            {popupData.noOfTempSensors >= 6 && (
                                              <>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts5 ? Number(log.Ts5).toFixed(2) : "N/A"}
                                                </td>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts6 ? Number(log.Ts6).toFixed(2) : "N/A"}
                                                </td>
                                                <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                  {log.Ts5 && log.Ts6
                                                    ? Number(Math.abs(log.Ts5 - log.Ts6)).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                              </>
                                            )}
                                            {popupData.noOfTempSensors >= 2 && isFirstInCoach && (
                                              <>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.LHSideAxleAvg
                                                    ? Number(tempCalcs.LHSideAxleAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.RHSideAxleAvg
                                                    ? Number(tempCalcs.RHSideAxleAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.WholeCoachAxleAvg
                                                    ? Number(tempCalcs.WholeCoachAxleAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                              </>
                                            )}
                                            {popupData.noOfTempSensors >= 4 && isFirstInCoach && (
                                              <>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.LHSideWheelAvg
                                                    ? Number(tempCalcs.LHSideWheelAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.RHSideWheelAvg
                                                    ? Number(tempCalcs.RHSideWheelAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.WholeCoachWheelAvg
                                                    ? Number(tempCalcs.WholeCoachWheelAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                              </>
                                            )}
                                            {popupData.noOfTempSensors >= 6 && isFirstInCoach && (
                                              <>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.LHSideBrakeAvg
                                                    ? Number(tempCalcs.LHSideBrakeAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.RHSideBrakeAvg
                                                    ? Number(tempCalcs.RHSideBrakeAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                                <td
                                                  className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                  rowSpan={sortedLogs.length}
                                                >
                                                  {tempCalcs?.WholeCoachBrakeAvg
                                                    ? Number(tempCalcs.WholeCoachBrakeAvg).toFixed(2)
                                                    : "N/A"}
                                                </td>
                                              </>
                                            )}
                                          </tr>
                                        );
                                      });
                                    }
                                    return rows;
                                  } else {
                                    return sortedLogs.map((log, logIndex) => {
                                      const isFirstRowForCoach = logIndex === 0;
                                      return (
                                        <tr key={`${coachIndex}-${logIndex}`} className="hover:bg-gray-50">
                                          <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{log.AxleNo || "N/A"}</td>
                                          <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                            {log.AxleSpeed ? Number(log.AxleSpeed).toFixed(2) : "N/A"}
                                          </td>
                                          {isFirstRowForCoach ? (
                                            <td
                                              className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle"
                                              rowSpan={sortedLogs.length}
                                            >
                                              {coach.CoachPosition || "N/A"}
                                            </td>
                                          ) : null}
                                          {isFirstRowForCoach ? (
                                            <td
                                              className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle"
                                              rowSpan={sortedLogs.length}
                                            >
                                              {log.CoachNo || "N/A"}
                                            </td>
                                          ) : null}
                                          <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                            {log.Ts1 ? Number(log.Ts1).toFixed(2) : "N/A"}
                                          </td>
                                          <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                            {log.Ts2 ? Number(log.Ts2).toFixed(2) : "N/A"}
                                          </td>
                                          <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                            {log.Ts1 && log.Ts2
                                              ? Number(Math.abs(log.Ts1 - log.Ts2)).toFixed(2)
                                              : "N/A"}
                                          </td>
                                          {popupData.noOfTempSensors >= 4 && (
                                            <>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts3 ? Number(log.Ts3).toFixed(2) : "N/A"}
                                              </td>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts4 ? Number(log.Ts4).toFixed(2) : "N/A"}
                                              </td>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts3 && log.Ts4
                                                  ? Number(Math.abs(log.Ts3 - log.Ts4)).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                            </>
                                          )}
                                          {popupData.noOfTempSensors >= 6 && (
                                            <>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts5 ? Number(log.Ts5).toFixed(2) : "N/A"}
                                              </td>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts6 ? Number(log.Ts6).toFixed(2) : "N/A"}
                                              </td>
                                              <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                                {log.Ts5 && log.Ts6
                                                  ? Number(Math.abs(log.Ts5 - log.Ts6)).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                            </>
                                          )}
                                          {popupData.noOfTempSensors >= 2 && isFirstRowForCoach && (
                                            <>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.LHSideAxleAvg
                                                  ? Number(tempCalcs.LHSideAxleAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.RHSideAxleAvg
                                                  ? Number(tempCalcs.RHSideAxleAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.WholeCoachAxleAvg
                                                  ? Number(tempCalcs.WholeCoachAxleAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                            </>
                                          )}
                                          {popupData.noOfTempSensors >= 4 && isFirstRowForCoach && (
                                            <>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.LHSideWheelAvg
                                                  ? Number(tempCalcs.LHSideWheelAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.RHSideWheelAvg
                                                  ? Number(tempCalcs.RHSideWheelAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.WholeCoachWheelAvg
                                                  ? Number(tempCalcs.WholeCoachWheelAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                            </>
                                          )}
                                          {popupData.noOfTempSensors >= 6 && isFirstRowForCoach && (
                                            <>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.LHSideBrakeAvg
                                                  ? Number(tempCalcs.LHSideBrakeAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.RHSideBrakeAvg
                                                  ? Number(tempCalcs.RHSideBrakeAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                              <td
                                                className="px-1 py-2 text-[11px] text-gray-900 text-center align-middle bg-blue-50"
                                                rowSpan={sortedLogs.length}
                                              >
                                                {tempCalcs?.WholeCoachBrakeAvg
                                                  ? Number(tempCalcs.WholeCoachBrakeAvg).toFixed(2)
                                                  : "N/A"}
                                              </td>
                                            </>
                                          )}
                                        </tr>
                                      );
                                    });
                                  }
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-[11px] text-gray-600 text-center py-2">No valid temperature log data available</p>
                        )}
                      </div>
                    </div>

                    {popupData.alarms && popupData.alarms.length > 0 && (
                      <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-red-50 to-white">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <h3 className="text-base font-semibold text-gray-900">
                              Alarms ({popupData.alarms.length})
                            </h3>
                          </div>
                        </div>
                        <div className="px-2 py-3">
                          <div className="lg:overflow-x-hidden">
                            <table className="min-w-full divide-y divide-gray-200 lg:table-auto">
                              <thead className="bg-red-50">
                                <tr>
                                  {[
                                    "Axle No",
                                    "Coach No",
                                    "Coach Position",
                                    "Side",
                                    "Temperature",
                                    "Acknowledged By",
                                    "Acknowledgement Time",
                                  ].map((head, i) => (
                                    <th
                                      key={i}
                                      className="px-1 py-2 text-center text-[11px] font-medium text-red-800 uppercase tracking-wider"
                                    >
                                      {head}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {popupData.alarms.map((alarm, index) => (
                                  <tr key={index} className="hover:bg-red-50">
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{alarm.AxleNo || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{alarm.CoachNo || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{alarm.CoachPosition || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{mapSide(alarm.Side)}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {alarm.Temperature ? `${Number(alarm.Temperature).toFixed(2)}°C` : "N/A"}
                                    </td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {alarm.AcknowledgedBy || "Not Yet Acknowledged"}
                                    </td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {formatAcknowledgementTime(alarm.AcknowledgementTime)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {popupData.warnings && popupData.warnings.length > 0 && (
                      <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-3 py-2 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-white">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                            <h3 className="text-base font-semibold text-gray-900">
                              Warnings ({popupData.warnings.length})
                            </h3>
                          </div>
                        </div>
                        <div className="px-2 py-3">
                          <div className="lg:overflow-x-hidden">
                            <table className="min-w-full divide-y divide-gray-200 lg:table-auto">
                              <thead className="bg-yellow-50">
                                <tr>
                                  {[
                                    "Axle No",
                                    "Coach No",
                                    "Coach Position",
                                    "Side",
                                    "Temperature",
                                    "Acknowledged By",
                                    "Acknowledgement Time",
                                  ].map((head, i) => (
                                    <th
                                      key={i}
                                      className="px-1 py-2 text-center text-[11px] font-medium text-yellow-800 uppercase tracking-wider"
                                    >
                                      {head}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {popupData.warnings.map((warning, index) => (
                                  <tr key={index} className="hover:bg-yellow-50">
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{warning.AxleNo || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{warning.CoachNo || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{warning.CoachPosition || "N/A"}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">{mapSide(warning.Side)}</td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {warning.Temperature ? `${Number(warning.Temperature).toFixed(2)}°C` : "N/A"}
                                    </td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {warning.AcknowledgedBy || "Not Yet Acknowledged"}
                                    </td>
                                    <td className="px-1 py-2 text-[11px] text-gray-900 text-center">
                                      {formatAcknowledgementTime(warning.AcknowledgementTime)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrainDetailsTable;